/**
 * AI Service Configuration
 * Centralized configuration for AI services (OpenAI, document processing, etc.)
 */

/**
 * AI operation types for model selection
 */
export type AIOperation =
  | "extraction"
  | "summarization"
  | "chat"
  | "risk-assessment"
  | "soap-generation" // Ambient Documentation: Generate SOAP notes
  | "transcript-enhancement" // Ambient Documentation: Enhance medical transcripts
  | "icd-suggestion" // Ambient Documentation: Suggest ICD-10 codes
  | "pa-detection" // Prior Auth: Detect PA requirements
  | "clinical-justification" // Prior Auth: Generate clinical justification
  | "DEFAULT";

/**
 * OpenAI model configuration
 */
export const AI_MODELS = {
  GPT4: "openai/gpt-4o",
  GPT4_TURBO: "openai/gpt-4-turbo",
  GPT35_TURBO: "openai/gpt-3.5-turbo",
  GPT5: "openai/gpt-5.4",
  CLAUDE_SONNET: "anthropic/claude-sonnet-4.6",

  DEFAULT: "openai/gpt-4o",
  EXTRACTION: "openai/gpt-4o",
  SUMMARIZATION: "anthropic/claude-sonnet-4.6",
  CLASSIFICATION: "openai/gpt-3.5-turbo",
} as const;

/**
 * Temperature settings for different AI operations
 */
export const AI_TEMPERATURES = {
  // Lower temperature for factual extraction
  EXTRACTION: 0.1,

  // Medium temperature for summarization
  SUMMARIZATION: 0.3,

  // Higher temperature for creative tasks
  CREATIVE: 0.7,

  // Deterministic (lowest variation)
  DETERMINISTIC: 0.0,

  // Balanced
  BALANCED: 0.5,
} as const;

/**
 * Token limits for different operations
 */
export const AI_TOKEN_LIMITS = {
  EXTRACTION: 2000,
  SUMMARIZATION: 1500,
  CHAT: 4000,
  LONG_FORM: 8000,
  SOAP_GENERATION: 4000,
  TRANSCRIPT_ENHANCEMENT: 3000,
  ICD_SUGGESTION: 2000,
  PA_DETECTION: 1500,
  CLINICAL_JUSTIFICATION: 2500,
} as const;

/**
 * Timeout settings (in milliseconds)
 */
export const AI_TIMEOUTS = {
  EXTRACTION: 30000, // 30 seconds
  SUMMARIZATION: 20000, // 20 seconds
  CHAT: 15000, // 15 seconds
  LONG_OPERATION: 60000, // 60 seconds
  SOAP_GENERATION: 45000, // 45 seconds - complex clinical documentation
  TRANSCRIPT_ENHANCEMENT: 30000, // 30 seconds
  ICD_SUGGESTION: 20000, // 20 seconds
} as const;

/**
 * Retry configuration
 */
export const AI_RETRY_CONFIG = {
  MAX_RETRIES: 3,
  INITIAL_DELAY: 1000, // 1 second
  MAX_DELAY: 10000, // 10 seconds
  BACKOFF_MULTIPLIER: 2,
} as const;

/**
 * Confidence thresholds
 */
export const AI_CONFIDENCE_THRESHOLDS = {
  HIGH: 0.9,
  MEDIUM: 0.7,
  LOW: 0.5,
  MINIMUM_ACCEPTABLE: 0.6,
} as const;

/**
 * Document processing configuration
 */
export const DOCUMENT_PROCESSING = {
  MAX_FILE_SIZE: 10 * 1024 * 1024, // 10MB
  MAX_TEXT_LENGTH: 50000, // characters
  SUPPORTED_FORMATS: ["pdf", "txt", "doc", "docx", "jpg", "png"],
  CHUNK_SIZE: 4000, // for large documents
} as const;

/**
 * AI prompts templates
 */
export const AI_PROMPTS = {
  PATIENT_EXTRACTION: `You are a medical AI assistant. Extract patient information from the following clinical document.
Return a structured JSON object with the following fields:
- firstName, lastName, dateOfBirth, gender, email, phone
- allergies (array), medications (array with name, dosage, frequency)
- conditions (array), chiefComplaint
- Any other relevant medical information

Document:
{documentText}

Return only valid JSON with extracted data and confidence scores.`,

  CLINICAL_SUMMARY: `Summarize the following clinical information for a healthcare provider.
Focus on:
- Key diagnoses and conditions
- Current medications and treatments
- Recent test results and findings
- Risk factors and alerts
- Recommended next steps

Clinical data:
{clinicalData}

Provide a concise, professional summary.`,

  RISK_ASSESSMENT: `Analyze the patient data and identify potential health risks.
Consider:
- Medical history and current conditions
- Medications and potential interactions
- Vital signs and lab results
- Social and family history

Patient data:
{patientData}

Return risks with severity levels (low, medium, high, critical).`,
} as const;

/**
 * Feature flags for AI functionality
 */
export const AI_FEATURES = {
  ENABLE_EXTRACTION: true,
  ENABLE_SUMMARIZATION: true,
  ENABLE_RISK_ASSESSMENT: true,
  ENABLE_DRUG_INTERACTION_CHECK: true,
  ENABLE_CLINICAL_INSIGHTS: true,
  FALLBACK_TO_SIMULATION: true, // Fall back to mock data if AI unavailable
} as const;

/**
 * Get AI configuration for a specific operation
 */
export function getAIConfig(operation: AIOperation = "DEFAULT") {
  switch (operation) {
    case "extraction":
      return {
        model: AI_MODELS.EXTRACTION,
        temperature: AI_TEMPERATURES.EXTRACTION,
        maxOutputTokens: AI_TOKEN_LIMITS.EXTRACTION,
        timeout: AI_TIMEOUTS.EXTRACTION,
      };
    case "summarization":
      return {
        model: AI_MODELS.SUMMARIZATION,
        temperature: AI_TEMPERATURES.SUMMARIZATION,
        maxOutputTokens: AI_TOKEN_LIMITS.SUMMARIZATION,
        timeout: AI_TIMEOUTS.SUMMARIZATION,
      };
    case "chat":
      return {
        model: AI_MODELS.DEFAULT,
        temperature: AI_TEMPERATURES.BALANCED,
        maxOutputTokens: AI_TOKEN_LIMITS.CHAT,
        timeout: AI_TIMEOUTS.CHAT,
      };
    case "risk-assessment":
      return {
        model: AI_MODELS.DEFAULT,
        temperature: AI_TEMPERATURES.DETERMINISTIC,
        maxOutputTokens: AI_TOKEN_LIMITS.SUMMARIZATION,
        timeout: AI_TIMEOUTS.EXTRACTION,
      };
    // Ambient Documentation operations
    case "soap-generation":
      return {
        model: AI_MODELS.CLAUDE_SONNET,
        temperature: AI_TEMPERATURES.SUMMARIZATION, // 0.3 for clinical accuracy
        maxOutputTokens: AI_TOKEN_LIMITS.SOAP_GENERATION,
        timeout: AI_TIMEOUTS.SOAP_GENERATION,
      };
    case "transcript-enhancement":
      return {
        model: AI_MODELS.GPT4,
        temperature: AI_TEMPERATURES.EXTRACTION, // 0.1 for accuracy
        maxOutputTokens: AI_TOKEN_LIMITS.TRANSCRIPT_ENHANCEMENT,
        timeout: AI_TIMEOUTS.TRANSCRIPT_ENHANCEMENT,
      };
    case "icd-suggestion":
      return {
        model: AI_MODELS.GPT4,
        temperature: AI_TEMPERATURES.DETERMINISTIC, // 0.0 for precision
        maxOutputTokens: AI_TOKEN_LIMITS.ICD_SUGGESTION,
        timeout: AI_TIMEOUTS.ICD_SUGGESTION,
      };
    // Prior Authorization operations
    case "pa-detection":
      return {
        model: AI_MODELS.GPT4,
        temperature: AI_TEMPERATURES.DETERMINISTIC,
        maxOutputTokens: AI_TOKEN_LIMITS.PA_DETECTION,
        timeout: AI_TIMEOUTS.EXTRACTION,
      };
    case "clinical-justification":
      return {
        model: AI_MODELS.GPT4,
        temperature: AI_TEMPERATURES.SUMMARIZATION,
        maxOutputTokens: AI_TOKEN_LIMITS.CLINICAL_JUSTIFICATION,
        timeout: AI_TIMEOUTS.SUMMARIZATION,
      };
    default:
      return {
        model: AI_MODELS.DEFAULT,
        temperature: AI_TEMPERATURES.BALANCED,
        maxOutputTokens: AI_TOKEN_LIMITS.EXTRACTION,
        timeout: AI_TIMEOUTS.EXTRACTION,
      };
  }
}

/**
 * Check if AI service should use simulation mode
 */
export function shouldUseSimulation(): boolean {
  // Import features from centralized env config
  // Using dynamic import to avoid circular dependencies
  // eslint-disable-next-line @typescript-eslint/no-require-imports
  const { features } = require("./env");
  return !features.hasOpenAI && AI_FEATURES.FALLBACK_TO_SIMULATION;
}
